import { useAttributeCRUDActions } from '@mm/state/actions/AttributeCRUDActions';
import { useEntryCRUDActions } from '@mm/state/actions/EntryCRUDActions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import AttributeModel from '@mm/state/model/AttributeModel';
import { FormEntry } from '@mm/state/model/FormsModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import { selectEnv } from '@mm/state/selectors/envSelectors';
import { selectActiveFacebookPage } from '@mm/state/selectors/facebookPageSelector';
import { hasConnectedRealFacebookPageSelector } from '@mm/state/selectors/userSelector';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useSelector } from 'react-redux';

export const getAttributeName = (
  id: number | undefined,
  variableType: 'Variable' | 'SystemVariable' | undefined,
  attributes: AttributeModel[],
) => {
  if (id && variableType) {
    const attribute = attributes.find(
      a => a.id === id && (variableType === 'Variable') !== !!a.system,
    );
    return attribute && attribute.name ? attribute.name : '';
  }

  return '';
};

export const getBotAttributes = (
  allAttributes: AttributeModel[],
  includeSettableSystemVars: boolean,
) =>
  allAttributes.filter(
    attr => !attr.system || (includeSettableSystemVars && attr.settable),
  );

export const useAttributeHandlers = (
  includeSettableSystemVars,
  updateVariableType = true,
  keepFbOnlySysAttributes = true,
) => {
  const { create: createAttribute } = useAttributeCRUDActions();
  const { update: updateEntry } = useEntryCRUDActions();
  const { update: updateWidget } = useWidgetCRUDActions();

  const activeFacebookPageId = useSelector((state: StoreState) =>
    activeFacebookPageIdSelector(state),
  );

  const connectedPage = useSelector(selectActiveFacebookPage);
  const isUserPageConnected = useSelector(hasConnectedRealFacebookPageSelector);

  const { smsActivated, instagramActivated, webChatActivated } =
    connectedPage || {};

  const { sysVariablesAllowedToAssign } = useSelector(selectEnv);

  const removeAttributeForFb =
    isUserPageConnected &&
    !(smsActivated || instagramActivated || webChatActivated);

  const settableAttributes = useSelector((state: StoreState) =>
    getBotAttributes(
      state.attribute.entities,
      includeSettableSystemVars,
    ).filter(a =>
      keepFbOnlySysAttributes || !a.system
        ? a
        : removeAttributeForFb
        ? !!Object.entries(sysVariablesAllowedToAssign)
            .filter(([_, attrs]) => attrs.includes(a.name.replace(' ', '_')))
            .find(([p, _]) => p === 'facebook')
        : a,
    ),
  );

  const updateAttribute = async (
    widget: WidgetModel,
    attribute: AttributeModel,
    formEntry?: FormEntry,
  ) => {
    const { config } = widget;
    const variableType = attribute.system ? 'SystemVariable' : 'Variable';

    if (formEntry) {
      await updateEntry(widget.id, {
        ...formEntry,
        variableId: attribute.id,
        ...(updateVariableType ? { variableType } : {}),
      });
    } else {
      await updateWidget({
        ...widget,
        config: {
          ...config,
          variableId: attribute.id,
          ...(updateVariableType ? { variableType } : {}),
        },
      });
    }
  };

  const createAttributeAndUpdateWidget = async (
    widget: WidgetModel,
    attributeInput: string,
    formEntry?: FormEntry,
  ) => {
    const attribute = (await createAttribute(
      activeFacebookPageId,
      { name: attributeInput },
      true,
    )) as any as AttributeModel;

    if (attribute) {
      await updateAttribute(widget, attribute, formEntry);
    }
  };

  return {
    createAttributeAndUpdateWidget,
    updateAttribute,
    settableAttributes,
  };
};
